import { RiFireFill, RiImage2Line, RiPrinterFill } from '@remixicon/react'
import { format, parseISO } from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'
// Ensure useRef is imported
import QRCode from 'react-qr-code'
import { useReactToPrint } from 'react-to-print'

import { Button } from '../ui/button'

import type { Bundle } from '@/api/bundles/bundles-types'
import type { Detail } from '@/api/orders/orders-types'
import { Card } from '@/components/ui/card'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/components/ui/carousel'
import { Separator } from '@/components/ui/separator'
import { DATE_FORMATS } from '@/constants/app'
import { useCachedOrdersSkids } from '@/hooks/use-cached-orders-skids'
import { cn } from '@/lib/utils'
import { AddBundleButton } from '@/pages/packaging/order/components/bundles-table/add-bundle'
import { UpdateBundle } from '@/pages/packaging/order/components/bundles-table/update-bundle'
import { useImageCacheStore } from '@/store/image-cache'
import { usePrintStickers } from '@/store/print-stickers'
import { getOriginalDetailImage } from '@/utils/details'
import { isPresignedUrl } from '@/utils/url'
import { formatSkidDisplayName } from '@/utils/skid-input-resolve'

interface BundleStickersProps {
  detail: Detail
}

/** Plain img + placeholder for stickers (avoids print-preview issues with layered ImageWithFallback). */
const StickerPreviewImage = ({ detail, className }: { detail: Detail; className?: string }) => {
  const bustParam = useImageCacheStore((s) => s.getBustParam(detail.autoid))
  const imageUrl = getOriginalDetailImage(detail, bustParam)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [imageUrl])

  if (!imageUrl?.trim() || failed) {
    return (
      <div className={cn('flex size-full flex-col items-center justify-center gap-2', className)}>
        <RiImage2Line className='text-grey-300 size-8' />
        <span className='text-grey-500 text-xs'>Image Not Found</span>
      </div>
    )
  }

  return (
    <img
      src={imageUrl}
      crossOrigin={isPresignedUrl(imageUrl) ? 'anonymous' : undefined}
      alt={String(detail.autoid)}
      className={className}
      onError={() => setFailed(true)}
    />
  )
}

const getBundleStickerLink = (detail: Detail, bundle: Bundle) => {
  const baseUrl = window.location.origin

  const link =
    baseUrl +
    `/packaging/?bundle=${bundle.id}&search=${detail.invoice}&detail=${detail.autoid}&ship-date=all`

  return link
}

/** The bundle's shipping unit (e.g. a box), so the sticker stands in for a unit label. */
const ShippingUnitField = ({ unitLabel }: { unitLabel: string }) =>
  unitLabel ? (
    <>
      <Separator
        className='h-5'
        orientation='vertical'
      />
      <div className='flex flex-col gap-px leading-none'>
        <div className='text-xs leading-none text-[#000]'>Unit</div>
        <div className='text-xs'>{unitLabel}</div>
      </div>
    </>
  ) : null

const PrintableBundleCard = React.forwardRef<
  HTMLDivElement,
  { detail: Detail; bundle: Bundle; current: number; count: number; unitLabel: string }
>(({ detail, bundle, current, count, unitLabel }, ref) => {
  const link = getBundleStickerLink(detail, bundle)

  return (
    <Card
      ref={ref}
      className='mx-auto gap-2.5 rounded-md border-none p-4 font-semibold'
      style={{ width: '6in', height: '4in', boxSizing: 'border-box' }}
    >
      <div className='flex h-8 items-center gap-2.5'>
        <div className='flex flex-col gap-px leading-none'>
          <div className='text-xs text-[#000]'>Customer</div>
          <div className='text-xs'>{detail?.customer ?? '-'}</div>
        </div>
        <Separator
          className='h-5'
          orientation='vertical'
        />
        <div className='flex flex-col gap-px leading-none'>
          <div className='text-xs text-[#000]'>Ship tag</div>
          <div className='text-xs'>{detail?.ship_tag ?? '-'}</div>
        </div>
        <Separator
          className='h-5'
          orientation='vertical'
        />
        <div className='flex max-w-20 flex-col gap-px leading-none'>
          <div className='text-xs text-[#000]'>Bundle qty</div>
          <div className='text-xs'>{bundle?.quantity ?? '-'}</div>
        </div>
        <div className='ml-auto flex h-full items-center justify-center self-start rounded-sm border-2 border-[#000] p-1 text-[20px] font-semibold'>
          {current} of {count}
        </div>
      </div>
      <div className='flex items-start gap-2.5'>
        <div className='w-full space-y-2'>
          <div className='flex h-7 items-center gap-2.5'>
            <div className='flex flex-col gap-px'>
              <div className='text-xs leading-none text-[#433131]'>Order</div>
              <div className='text-xs'>#{detail?.invoice ?? '-'}</div>
            </div>
            <Separator
              className='h-5'
              orientation='vertical'
            />
            <div className='flex flex-col gap-px'>
              <div className='text-xs leading-none text-[#000]'>Product</div>
              <div className='text-xs'>{detail?.inven}</div>
            </div>
            <Separator
              className='h-5'
              orientation='vertical'
            />
            <div className='flex flex-col gap-px'>
              <div className='text-xs leading-none text-[#000]'>Qty</div>
              <div className='text-xs'>{detail?.quan}</div>
            </div>
            <Separator
              className='h-5'
              orientation='vertical'
            />
            <div className='flex flex-col gap-px'>
              <div className='text-xs leading-none text-[#000]'>Color</div>
              <div className='text-xs'>{detail?.color ?? '-'}</div>
            </div>
            <Separator
              className='h-5'
              orientation='vertical'
            />
            <div className='flex max-w-60 flex-col gap-px'>
              <div className='text-xs leading-none text-[#000]'>Blank size</div>
              <div className='text-xs'>
                {detail.width || '-'} x{' '}
                <span className={cn(detail.heightd !== 120 ? 'font-bold' : '')}>{detail.heightd}</span>
              </div>
            </div>
            <div className='ml-auto justify-self-end pt-1 text-[20px] font-semibold'>ID: {bundle.id}</div>
          </div>
          <div className='flex items-end gap-2.5 leading-none'>
            <div className='flex max-w-78 flex-col gap-1'>
              <div className='text-xs leading-none text-[#000]'>Description</div>
              <div className='mt-auto line-clamp-2 text-xs'>{detail?.descr ?? '-'}</div>
            </div>

            <Separator
              className='h-5'
              orientation='vertical'
            />

            <div className='flex flex-col gap-px leading-none'>
              <div className='text-xs leading-none text-[#000]'>Shipping</div>
              <div className='flex items-center gap-2.5'>
                <span className='text-xs'>
                  {detail.ship_date ? format(parseISO(detail.ship_date), DATE_FORMATS.dots) : '-'} {detail?.ship_time}
                </span>
                <Separator
                  className='h-3'
                  orientation='vertical'
                />
                <span
                  className={cn(
                    'flex items-center gap-px rounded-sm',
                    detail?.ship_type === 'SET OUTSIDE'
                      ? 'text-white-100 bg-[#000] py-0.5 pr-1 pl-0.5 text-xs leading-none'
                      : 'text-xs'
                  )}
                >
                  {detail?.ship_type === 'SET OUTSIDE' ? <RiFireFill className='-mt-px size-2.5' /> : null}

                  <span className='mt-px'>{detail?.ship_type ?? '-'}</span>
                </span>
              </div>
            </div>
            <ShippingUnitField unitLabel={unitLabel} />
          </div>{' '}
        </div>
      </div>
      <div className='mt-auto flex items-center justify-between gap-2.5'>
        <div className='img-block h-55 w-full rounded-md border border-[#000] p-1'>
          <StickerPreviewImage
            detail={detail}
            className='size-full object-contain'
          />
        </div>
        <div className='mt-auto flex h-full flex-col justify-end'>
          <QRCode
            onClick={() => window.open(link)}
            className='mt-2.5 size-25 rounded-sm'
            value={link}
          />
        </div>
      </div>
    </Card>
  )
})

const PrintableStickerWrapper = React.forwardRef<
  HTMLDivElement,
  {
    detail: Detail
    bundlesToPrint: Bundle[]
    current: number
    count: number
    getUnitLabel: (bundle: Bundle) => string
  }
>(({ detail, bundlesToPrint, getUnitLabel }, ref) => {
  return (
    <div ref={ref}>
      {' '}
      {/* This is the div that useReactToPrint will reference */}
      {bundlesToPrint.map((bundle, index) => (
        <React.Fragment key={`printable-bundle-${index}`}>
          <PrintableBundleCard
            current={detail.bundles?.findIndex((bun) => bun.id === bundle.id)! + 1}
            count={detail.bundles?.length ?? 0}
            detail={detail}
            bundle={bundle}
            unitLabel={getUnitLabel(bundle)}
          />
          {index < bundlesToPrint.length - 1 && <div className='page-break-after'></div>}
        </React.Fragment>
      ))}
    </div>
  )
})

export const BundleStickers = ({ detail }: BundleStickersProps) => {
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(detail?.bundles?.length ?? 0)

  const [bundlesForPrinting, setBundlesForPrinting] = useState<Bundle[]>([])
  const printContentRef = useRef<HTMLDivElement>(null)
  const skids = useCachedOrdersSkids()
  const getUnitLabel = (bundle: Bundle) => formatSkidDisplayName(bundle.skid_id, skids)

  useEffect(() => {
    setCount(detail?.bundles?.length ?? 0)
  }, [detail.bundles?.length])

  useEffect(() => {
    if (!api) {
      return
    }

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap() + 1)

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api])

  const stickerPageStyle = `
@page {
  size: 6in 4in;
  margin: 0;
}

@media print {
  * {
    font-family: Arial, sans-serif !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  html, body {
    margin: 0 !important;
    padding: 0 !important;
  }

  body { zoom: 1 !important; }

  img {
    image-rendering: crisp-edges;
    -ms-interpolation-mode: nearest-neighbor;
  }

  /* Hide interactive UI */
  .no-print { display: none !important; }

  /* Printable card exact size */
  .printable-card {
    width: 6in !important;
    height: 4in !important;
    box-sizing: border-box !important;
    padding: 0.14in !important; /* ~0.625rem visual */
    margin: 0 auto !important;
    border: none !important;
    border-radius: 0 !important;
    background: #fff !important;
    overflow: hidden !important;
  }

  .page-break-after {
    page-break-after: always;
    break-after: page;
    height: 0 !important;
    overflow: hidden !important;
  }

  /* =========================================
     INCH SCALE (relative to 6in x 4in sticker)
     1 step = 0.05in
     ========================================= */
  :root {
    --st-0: 0in;
    --st-1: 0.05in;
    --st-2: 0.10in;
    --st-3: 0.15in;
    --st-4: 0.20in;
    --st-5: 0.25in;
    --st-6: 0.30in;
    --st-7: 0.35in;
    --st-8: 0.40in;
    --st-9: 0.45in;
    --st-10: 0.50in;
  }

  /* -------------------
     gap-* overrides
     ------------------- */
  .gap-0 { gap: var(--st-0) !important; }
  .gap-1 { gap: var(--st-1) !important; }
  .gap-2 { gap: var(--st-2) !important; }
  .gap-3 { gap: var(--st-3) !important; }
  .gap-4 { gap: var(--st-4) !important; }
  .gap-5 { gap: var(--st-5) !important; }
  .gap-6 { gap: var(--st-6) !important; }
  .gap-7 { gap: var(--st-7) !important; }
  .gap-8 { gap: var(--st-8) !important; }

  .gap-2\\.5 { gap: 0.1in !important; } /* between st-2 and st-3 */

  /* -------------------
     space-y-* overrides
     Tailwind uses > :not([hidden]) ~ :not([hidden])
     ------------------- */
  .space-y-0 > :not([hidden]) ~ :not([hidden]) { margin-top: var(--st-0) !important; }
  .space-y-1 > :not([hidden]) ~ :not([hidden]) { margin-top: var(--st-1) !important; }
  .space-y-2 > :not([hidden]) ~ :not([hidden]) { margin-top: var(--st-2) !important; }
  .space-y-3 > :not([hidden]) ~ :not([hidden]) { margin-top: var(--st-3) !important; }
  .space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: var(--st-4) !important; }
  .space-y-5 > :not([hidden]) ~ :not([hidden]) { margin-top: var(--st-5) !important; }
  .space-y-6 > :not([hidden]) ~ :not([hidden]) { margin-top: var(--st-6) !important; }

  /* -------------------
     h-* overrides (inches)
     These are the ones you actually use in the sticker
     ------------------- */
  .h-3  { height: 0.125in !important; }
  .h-5  { height: 0.21in !important; }
  .h-7  { height: 0.29in !important; }
  .h-8  { height: 0.33in !important; }

  /* Image block in your code: h-55 */
  .h-55 { height: 2.30in !important; }

  /* -------------------
     w-* / max-w-* overrides (optional)
     ------------------- */
  .max-w-20 { max-width: 0.85in !important; }
  .max-w-60 { max-width: 2.50in !important; }
  .max-w-78 { max-width: 3.25in !important; }

  /* -------------------
     size-* overrides
     (Tailwind size-25 => width/height)
     ------------------- */
  .size-25 { width: 1.05in !important; height: 1.05in !important; }

  /* -------------------
     text sizing overrides
     (you use text-xs and text-[20px])
     You can override those too if you want consistency.
     ------------------- */
  .text-xs { font-size: 9pt !important; }
  .text-\\[20px\\] { font-size: 18pt !important; }

  /* Ensure leading-none stays stable */
  .leading-none { line-height: 1 !important; }
}
`

  const { setPrintDetail, setPrintOrder } = usePrintStickers()

  const handlePrint = useReactToPrint({
    contentRef: printContentRef,
    documentTitle: `Bundle Stickers - ${detail?.invoice}`,
    pageStyle: stickerPageStyle,
    onBeforePrint: async () => {
      setTimeout(() => {
        setPrintDetail(null)
        setPrintOrder(null)
      }, 200)
    },
    onAfterPrint: async () => {
      setBundlesForPrinting([])
    }
  })

  // Call this function when "Print Current sticker" is clicked
  const printCurrentSticker = () => {
    const currentBundle = detail?.bundles?.[current - 1]
    if (currentBundle) {
      setBundlesForPrinting([currentBundle]) // Set only the current bundle
      setTimeout(() => {
        // Small delay to allow state update to render
        handlePrint()
      }, 0)
    }
  }

  const printAllStickers = () => {
    if (detail?.bundles) {
      setBundlesForPrinting(detail.bundles)
      setTimeout(() => {
        handlePrint()
      }, 0)
    }
  }

  return (
    <div className='w-full'>
      <Carousel
        className='no-print mx-auto w-150'
        setApi={setApi}
      >
        <CarouselContent>
          {detail?.bundles?.map((bundle, index) => {
            const link = getBundleStickerLink(detail, bundle)

            return (
              <CarouselItem key={index}>
                <Card
                  className='mx-auto gap-2.5 rounded-md p-4 font-semibold text-[#000]'
                  style={{ width: '6in', height: '4in', boxSizing: 'border-box' }}
                >
                  <div className='flex h-8 items-center gap-2.5'>
                    <div className='flex flex-col gap-px leading-none'>
                      <div className='text-xs text-[#000]'>Customer</div>
                      <div className='text-xs'>{detail?.customer ?? '-'}</div>
                    </div>
                    <Separator
                      className='h-5'
                      orientation='vertical'
                    />
                    <div className='flex flex-col gap-px leading-none'>
                      <div className='text-xs text-[#000]'>Ship tag</div>
                      <div className='text-xs'>{detail?.ship_tag ?? '-'}</div>
                    </div>
                    <Separator
                      className='h-5'
                      orientation='vertical'
                    />
                    <div className='flex max-w-20 flex-col gap-px leading-none'>
                      <div className='text-xs text-[#000]'>Bundle qty</div>
                      <UpdateBundle
                        key={bundle.quantity}
                        bundle={bundle}
                        className='h-5 grow-0 rounded-sm !text-xs [&_svg]:!size-3'
                      />
                    </div>

                    <div className='ml-auto flex h-full items-center justify-center self-start rounded-sm border-2 border-[#000] p-1 text-[20px] font-semibold'>
                      {current} of {count}
                    </div>
                  </div>
                  <div className='flex items-start gap-2.5'>
                    <div className='w-full space-y-2'>
                      <div className='flex h-7 items-center gap-2.5'>
                        <div className='flex flex-col gap-px'>
                          <div className='text-xs leading-none text-[#000]'>Order</div>
                          <div className='text-xs'>#{detail?.invoice ?? '-'}</div>
                        </div>
                        <Separator
                          className='h-5'
                          orientation='vertical'
                        />
                        <div className='flex flex-col gap-px'>
                          <div className='text-xs leading-none text-[#000]'>Product</div>
                          <div className='text-xs'>{detail?.inven}</div>
                        </div>
                        <Separator
                          className='h-5'
                          orientation='vertical'
                        />
                        <div className='flex flex-col gap-px'>
                          <div className='text-xs leading-none text-[#000]'>Qty</div>
                          <div className='text-xs'>{detail?.quan}</div>
                        </div>
                        <Separator
                          className='h-5'
                          orientation='vertical'
                        />
                        <div className='flex flex-col gap-px'>
                          <div className='text-xs leading-none text-[#000]'>Color</div>
                          <div className='text-xs'>{detail?.color ?? '-'}</div>
                        </div>
                        <Separator
                          className='h-5'
                          orientation='vertical'
                        />
                        <div className='flex max-w-60 flex-col gap-px'>
                          <div className='text-xs leading-none text-[#000]'>Blank size</div>
                          <div className='text-xs'>
                            {detail.width || '-'} x{' '}
                            <span className={cn(detail.heightd !== 120 ? 'font-bold' : '')}>
                              {detail.heightd}
                            </span>
                          </div>
                        </div>
                        <div className='ml-auto justify-self-end pt-1 text-[20px] font-semibold'>
                          ID: {bundle.id}
                        </div>
                      </div>
                      <div className='flex items-end gap-2.5 leading-none'>
                        <div className='flex max-w-78 flex-col gap-1'>
                          <div className='text-xs leading-none text-[#000]'>Description</div>
                          <div className='mt-auto line-clamp-2 text-xs'>{detail?.descr ?? '-'}</div>
                        </div>

                        <Separator
                          className='h-5'
                          orientation='vertical'
                        />

                        <div className='flex flex-col gap-px leading-none'>
                          <div className='text-xs leading-none text-[#000]'>Shipping</div>
                          <div className='flex items-center gap-2.5'>
                            <span className='text-xs'>
                              {detail.ship_date ? format(parseISO(detail.ship_date), DATE_FORMATS.dots) : '-'} {detail?.ship_time}
                            </span>
                            <Separator
                              className='h-3'
                              orientation='vertical'
                            />
                            <span
                              className={cn(
                                'flex items-center gap-px rounded-sm',
                                detail?.ship_type === 'SET OUTSIDE'
                                  ? 'text-white-100 bg-[#000] py-0.5 pr-1 pl-0.5 text-xs leading-none'
                                  : 'text-xs'
                              )}
                            >
                              {detail?.ship_type === 'SET OUTSIDE' ? (
                                <RiFireFill className='-mt-px size-2.5' />
                              ) : null}

                              <span className='mt-px'>{detail?.ship_type ?? '-'}</span>
                            </span>
                          </div>
                        </div>
                        <ShippingUnitField unitLabel={getUnitLabel(bundle)} />
                      </div>{' '}
                    </div>
                  </div>
                  <div className='mt-auto flex items-center justify-between gap-2.5'>
                    <div className='h-55 w-full rounded-md border border-[#000] p-1'>
                      <StickerPreviewImage
                        detail={detail}
                        className='size-full object-contain'
                      />
                    </div>
                    <div className='mt-auto flex h-full flex-col justify-end'>
                      <QRCode
                        onClick={() => window.open(link)}
                        className='mt-2.5 size-25 rounded-sm'
                        value={link}
                      />
                    </div>
                  </div>
                </Card>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <CarouselPrevious />
        <CarouselNext />
      </Carousel>

      {bundlesForPrinting.length > 0 && (
        <div style={{ display: 'none' }}>
          <PrintableStickerWrapper
            current={current}
            count={count}
            ref={printContentRef}
            detail={detail}
            bundlesToPrint={bundlesForPrinting}
            getUnitLabel={getUnitLabel}
          />
        </div>
      )}

      <div className='mt-4 flex w-full items-end gap-2.5'>
        <AddBundleButton
          variant='outline'
          detail={detail}
          className={cn('text-black-100 border bg-transparent')}
        />
        <Button
          className='flex-1'
          variant='outline'
          onClick={printCurrentSticker}
        >
          <RiPrinterFill />
          <span>
            Print Current sticker — Bundle {current} of {count}
          </span>
        </Button>
        <Button
          className='flex-1'
          onClick={printAllStickers}
        >
          <RiPrinterFill />
          <span>Print All stickers</span>
        </Button>
      </div>
    </div>
  )
}
